import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { SelectItem } from '@/shared/components/ui/select';
import { FloatingLabelSelect } from '@/shared/components/custom/form-field';
import type { ProjectFolderNodeDto } from '../types';

interface MoveFileDialogProps {
  open: boolean;
  folders: ProjectFolderNodeDto[];
  onConfirm: (folderId: string | null) => void;
  onCancel: () => void;
}

function flattenFolders(nodes: ProjectFolderNodeDto[], depth = 0): { id: string; label: string }[] {
  return nodes.flatMap((node) => {
    if (!node.id || !node.name) return [];
    const prefix = '  '.repeat(depth);
    return [
      { id: node.id, label: `${prefix}${node.name}` },
      ...flattenFolders(node.children ?? [], depth + 1),
    ];
  });
}

export function MoveFileDialog({ open, folders, onConfirm, onCancel }: MoveFileDialogProps) {
  const { t } = useTranslation('projectFiles');
  const { t: tCommon } = useTranslation('common');
  const ROOT = '__root__';
  const [folderId, setFolderId] = useState(ROOT);

  const flatFolders = flattenFolders(folders);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onCancel();
      }}
    >
      <DialogContent data-testid="project-files-move-file-dialog">
        <DialogHeader>
          <DialogTitle>{t('item.moveDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <FloatingLabelSelect
            id="move-file-folder"
            label={t('item.moveDialog.folderLabel')}
            value={folderId}
            onValueChange={setFolderId}
            hasValue={true}
            data-testid="project-files-move-file-select"
          >
            <SelectItem value={ROOT}>{t('item.moveDialog.root')}</SelectItem>
            {flatFolders.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.label}
              </SelectItem>
            ))}
          </FloatingLabelSelect>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="project-files-move-file-cancel">
            {tCommon('actions.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => onConfirm(folderId === ROOT ? null : folderId)}
            data-testid="project-files-move-file-confirm"
          >
            {t('item.move')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
